import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Edit, Key, User, Eye, EyeOff, Shield, UserCheck, Clock, FileText, Search, Filter } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";
import { UserSearchDialog } from "./components/UserSearchDialog";
import { useLanguage } from "@/contexts/LanguageContext";

interface UserCredential {
  id: string;
  user_id: string;
  email: string;
  password_hash: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  profile?: {
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  user_type?: 'user' | 'client';
}

const UserCredentialsPage = () => {
  const { t } = useLanguage();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [selectedCredential, setSelectedCredential] = useState<UserCredential | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [notes, setNotes] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'user' | 'client'>('all');
  const queryClient = useQueryClient();

  // Fetch user credentials (admin only) - now includes both users and clients
  const { data: credentials, isLoading } = useQuery({
    queryKey: ["user-credentials", "profiles"],
    queryFn: async () => {
      // First get user credentials
      const { data: credentialsData, error: credentialsError } = await supabase
        .from("user_credentials")
        .select("*")
        .order("created_at", { ascending: false });

      if (credentialsError) throw credentialsError;

      // Get all profiles (includes both regular users and clients)
      // Exclude deleted and archived users
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, first_name, last_name, email")
        .not('email', 'like', 'deleted_%')
        .not('email', 'like', 'archived_%')
        .order("created_at", { ascending: false });

      if (profilesError) {
        console.warn("Could not fetch profiles:", profilesError);
      }

      // Get user roles to determine actual user type
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("user_id, role");

      // Helper to determine user type based on roles
      const getUserType = (userId: string): 'user' | 'client' => {
        const userRoles = rolesData?.filter(r => r.user_id === userId) || [];
        // If user has client role (from client_teams), they're a client
        // Check if they exist in client_teams table
        return userRoles.some(r => r.role === 'client') ? 'client' : 'user';
      };

      // For proper client detection, also fetch client_teams
      const { data: clientTeamsData } = await supabase
        .from("client_teams")
        .select("client_id");
      
      const clientIds = new Set(clientTeamsData?.map(ct => ct.client_id) || []);

      // Combine credentials with profiles and determine type
      const credentialsWithProfiles = credentialsData?.map(credential => ({
        ...credential,
        profile: profilesData?.find(profile => profile.id === credential.user_id) || null,
        user_type: clientIds.has(credential.user_id) ? 'client' as const : 'user' as const
      })) || [];

      // Add profiles that don't have credentials yet
      const profilesWithoutCredentials = profilesData?.filter(profile => 
        !credentialsData?.some(cred => cred.user_id === profile.id)
      ) || [];

      const profileCredentials = profilesWithoutCredentials.map(profile => ({
        id: `profile-${profile.id}`,
        user_id: profile.id,
        email: profile.email || '',
        password_hash: null,
        notes: 'No credentials record yet',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        profile: {
          full_name: profile.full_name,
          first_name: profile.first_name,
          last_name: profile.last_name
        },
        user_type: clientIds.has(profile.id) ? 'client' as const : 'user' as const
      }));

      return [...credentialsWithProfiles, ...profileCredentials] as UserCredential[];
    },
    enabled: isAdmin,
  });

  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: async ({ credentialId, password, notes: updatedNotes }: {
      credentialId: string;
      password: string;
      notes: string;
    }) => {
      // Use the send-credentials edge function to update password
      const { data, error } = await supabase.functions.invoke('send-credentials', {
        body: {
          action: 'update_password',
          user_id: selectedCredential!.user_id,
          new_password: password,
          notes: updatedNotes
        }
      });

      // Check for edge function errors
      if (error) throw error;
      
      // Check if the function returned an error in the response body
      if (data && !data.success) {
        throw new Error(data.error || 'Failed to update password');
      }

      // For users without existing credentials, create a new record
      if (credentialId.startsWith('profile-')) {
        const { error: insertError } = await supabase
          .from("user_credentials")
          .insert({
            user_id: selectedCredential!.user_id,
            credential_type: selectedCredential!.user_type === 'client' ? 'client_access' : 'system_access',
            status: 'active',
            notes: updatedNotes,
          });

        if (insertError) throw insertError;
      } else {
        // Update existing credentials record notes only
        const { error: credentialError } = await supabase
          .from("user_credentials")
          .update({
            notes: updatedNotes,
          })
          .eq("id", credentialId);

        if (credentialError) throw credentialError;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: t('passwordUpdated'),
        description: `${selectedCredential?.user_type === 'client' ? t('client') : t('systemUser')} ${t('passwordUpdatedSuccess')}`,
      });
      queryClient.invalidateQueries({ queryKey: ["user-credentials"] });
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      setIsEditDialogOpen(false);
      setNewPassword("");
      setNotes("");
      setSelectedCredential(null);
    },
    onError: (error: any) => {
      console.error('Password update error:', error);
      
      // Parse error response for better messages
      const errorMessage = error.message || t('failedToUpdatePassword');
      const isWeakPassword = errorMessage.includes('weak') || 
                            errorMessage.includes('pwned') ||
                            errorMessage.includes('easy to guess');
      
      toast({
        title: t('error'),
        description: isWeakPassword 
          ? t('weakPasswordError') || 'Password is too weak or has been compromised. Please choose a stronger password.'
          : errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleEditPassword = (credential: UserCredential) => {
    // Refresh data to ensure we have the latest email and info
    queryClient.invalidateQueries({ queryKey: ["user-credentials"] });
    queryClient.invalidateQueries({ queryKey: ["profiles"] });
    
    setSelectedCredential(credential);
    setNotes(credential.notes || "");
    setShowPassword(false);
    setIsEditDialogOpen(true);
  };

  const handleSavePassword = () => {
    if (!selectedCredential || !newPassword.trim()) {
      toast({
        title: t('error'),
        description: t('pleaseEnterPassword'),
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: t('error'),
        description: t('passwordMinLength'),
        variant: "destructive",
      });
      return;
    }

    updatePasswordMutation.mutate({
      credentialId: selectedCredential.id,
      password: newPassword,
      notes: notes,
    });
  };

  const getUserDisplayName = (credential: UserCredential) => {
    const profile = credential.profile;
    if (profile?.full_name) return profile.full_name;
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) return profile.first_name;
    return credential.email;
  };

  const getUserTypeLabel = (credential: UserCredential) => {
    return credential.user_type === 'client' ? t('client') : t('systemUser');
  };

  // Filter and search functionality
  const filteredCredentials = credentials?.filter(credential => {
    // Filter by user type
    if (userTypeFilter !== 'all' && credential.user_type !== userTypeFilter) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const displayName = getUserDisplayName(credential).toLowerCase();
      const email = credential.email.toLowerCase();
      const notes = credential.notes?.toLowerCase() || '';
      
      return displayName.includes(query) || 
             email.includes(query) || 
             notes.includes(query);
    }
    
    return true;
  }) || [];

  const getCredentialStats = () => {
    if (!credentials) return { total: 0, users: 0, clients: 0 };
    
    return {
      total: credentials.length,
      users: credentials.filter(c => c.user_type === 'user').length,
      clients: credentials.filter(c => c.user_type === 'client').length
    };
  };

  const stats = getCredentialStats();

  if (isLoadingRoles) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-gray-500">{t('loading')}</div>
        </div>
      </AppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Card className="p-8 text-center bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex flex-col items-center space-y-4">
              <Shield className="h-12 w-12 text-red-500" />
              <div>
                <h2 className="text-xl font-semibold text-red-800">{t('accessDenied')}</h2>
                <p className="text-red-600 mt-2">{t('restrictedToAdmins')}</p>
              </div>
            </div>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-primary/10 rounded-3xl -z-10"></div>
          <div className="p-8">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-br from-primary to-primary/80 rounded-xl shadow-lg">
                    <Key className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                      {t('userCredentialsPage')}
                    </h1>
                    <p className="text-lg text-muted-foreground">
                      {t('managePasswords')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-background/80 backdrop-blur-sm rounded-xl p-4 border shadow-sm">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-primary">{t('administratorPanel')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all duration-300 hover-scale">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">{t('totalAccounts')}</p>
                  <p className="text-3xl font-bold text-blue-800">{stats.total}</p>
                </div>
                <User className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all duration-300 hover-scale">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">{t('systemUsers')}</p>
                  <p className="text-3xl font-bold text-green-800">{stats.users}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all duration-300 hover-scale">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-medium">{t('clients')}</p>
                  <p className="text-3xl font-bold text-purple-800">{stats.clients}</p>
                </div>
                <Shield className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter Section */}
        <Card className="bg-gradient-to-r from-background to-muted/30 border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('searchUsers')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-background/50 border-primary/20 focus:border-primary/40"
                />
              </div>
              
              {/* User Type Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value as 'all' | 'user' | 'client')}
                  className="px-3 py-2 border rounded-md bg-background/50 border-primary/20 focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="all">{t('allTypes')}</option>
                  <option value="user">{t('systemUsers')}</option>
                  <option value="client">{t('clients')}</option>
                </select>
              </div>
              
              {/* Quick Search Dialog */}
              <UserSearchDialog
                credentials={credentials || []}
                isLoading={isLoading}
                onSelectUser={handleEditPassword}
              />
            </div>
            
            {searchQuery && (
              <div className="mt-3 text-sm text-muted-foreground">
                {t('foundResults')} {filteredCredentials.length} {t('resultsFor')} "{searchQuery}"
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credentials Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="text-muted-foreground text-lg">{t('loadingCredentials')}</div>
            </div>
          </div>
        ) : filteredCredentials.length === 0 ? (
          <Card className="bg-gradient-to-br from-muted/30 to-muted/50">
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <Search className="h-12 w-12 text-muted-foreground/50" />
                <div>
                  <h3 className="text-lg font-semibold text-muted-foreground">{t('noCredentialsFound')}</h3>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    {searchQuery ? `${t('noResultsFor')} "${searchQuery}"` : t('tryAdjustingFilters')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredCredentials.map((credential, index) => (
              <Card 
                key={credential.id} 
                className="group hover:shadow-xl transition-all duration-300 border-primary/10 hover:border-primary/30 bg-gradient-to-br from-background to-muted/20 hover-scale"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3 text-lg">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-background flex items-center justify-center ${
                          credential.user_type === 'client' 
                            ? 'bg-purple-500' 
                            : 'bg-green-500'
                        }`}>
                          {credential.user_type === 'client' ? (
                            <Shield className="h-2 w-2 text-white" />
                          ) : (
                            <UserCheck className="h-2 w-2 text-white" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{getUserDisplayName(credential)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          credential.user_type === 'client'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {getUserTypeLabel(credential)}
                        </span>
                      </div>
                    </CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground truncate" title={credential.email}>
                    {credential.email}
                  </p>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <Clock className="h-3 w-3 text-primary" />
                      <div>
                        <p className="text-muted-foreground">{t('created')}</p>
                        <p className="font-medium">{new Date(credential.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <Clock className="h-3 w-3 text-primary" />
                      <div>
                        <p className="text-muted-foreground">{t('updated')}</p>
                        <p className="font-medium">{new Date(credential.updated_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                  
                  {credential.notes && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        <Label className="text-xs text-muted-foreground">{t('notes')}</Label>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground break-words leading-relaxed">
                          {credential.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => handleEditPassword(credential)}
                    className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                    size="sm"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {t('changePassword')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Password Change Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-lg bg-gradient-to-br from-background to-muted/30 border-primary/20">
            <DialogHeader className="text-center pb-4">
              <DialogTitle className="text-2xl font-bold flex items-center justify-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary to-primary/80 rounded-lg">
                  <Key className="h-5 w-5 text-white" />
                </div>
                {t('changePassword')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* User Info Card */}
              <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background flex items-center justify-center ${
                        selectedCredential?.user_type === 'client' 
                          ? 'bg-purple-500' 
                          : 'bg-green-500'
                      }`}>
                        {selectedCredential?.user_type === 'client' ? (
                          <Shield className="h-2.5 w-2.5 text-white" />
                        ) : (
                          <UserCheck className="h-2.5 w-2.5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">
                          {selectedCredential ? getUserDisplayName(selectedCredential) : ""}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          selectedCredential?.user_type === 'client'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {selectedCredential?.user_type === 'client' ? t('client') : t('systemUser')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {selectedCredential?.email}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Password Input */}
              <div className="space-y-3">
                <Label htmlFor="new-password" className="text-sm font-semibold flex items-center gap-2">
                  <Key className="h-4 w-4 text-primary" />
                  {t('newPassword')}
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('enterNewPassword')}
                    minLength={6}
                    className="pr-12 bg-background/50 border-primary/20 focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-primary/10"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {newPassword && newPassword.length < 6 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <span className="h-2 w-2 bg-amber-500 rounded-full"></span>
                    {t('passwordMinLength')}
                  </p>
                )}
              </div>

              {/* Notes Input */}
              <div className="space-y-3">
                <Label htmlFor="notes" className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  {t('notesOptional')}
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('addNotesAboutChange')}
                  rows={3}
                  className="bg-background/50 border-primary/20 focus:border-primary/40 focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSavePassword}
                  disabled={updatePasswordMutation.isPending || !newPassword.trim() || newPassword.length < 6}
                  className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {updatePasswordMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('updating')}
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      {t('updatePassword')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setNewPassword("");
                    setShowPassword(false);
                    setNotes("");
                    setSelectedCredential(null);
                  }}
                  className="flex-1 border-primary/20 hover:bg-primary/5"
                >
                  {t('cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default UserCredentialsPage;
